import { MapPuzzleEndpoint } from '@/core/network/api/constants/api.url.constant';
import type { HttpClient } from '@/core/network/http/httpClient';
import type { GamResponse } from '@/core/network/http/httpClient.interface';
import type { AxiosResponse } from 'axios';

export interface MapPuzzleData {
  id: number;
  fen: string;
  setupMove: string;
  solutionMove: string;
  rating: number;
}

export interface TraceData {
  id: string;
  lat: number;
  lng: number;
  status: 'trace' | 'trace_solved';
  isMine: boolean;
  solvedByMe: boolean;
  solvedAt: string | null;
  expiresAt: string | null;
  publicAt: string | null;
  createdAt: string;
  puzzleId?: number;
  fen?: string;
  solutionUci?: string;
  rating?: number;
}

export interface GetTracesResponse {
  traces: TraceData[];
}

export interface OpenTraceResponse {
  id: string;
  status: 'trace' | 'trace_solved';
  opened: boolean;
  isMine: boolean;
  puzzle: MapPuzzleData;
}

export interface PreviewTraceResponse {
  id: string;
  status: 'trace' | 'trace_solved';
  isPreview: boolean;
  isMine: boolean;
  submittedMoveUci?: string | null;
  puzzle: MapPuzzleData;
}

export interface SolveTraceResponse {
  isCorrect: boolean;
  trace: TraceData;
}

export interface ViewTraceResponse {
  id: string;
  status: 'trace' | 'trace_solved';
  isMine: boolean;
  submittedMoveUci?: string | null;
  puzzle: MapPuzzleData;
}

export interface MapMarkerData {
  id: string;
  lat: number;
  lng: number;
  puzzleId: number | null;
  fen: string;
  solutionUci: string;
  rating: number;
  puzzleType: string;
  expiresAt: string;
  submittedMoveUci?: string | null;
  traceId?: string | null;
}

export class MapPuzzleApi {
  private readonly httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.httpClient = httpClient;
  }

  async getTraces(lat: number, lng: number): Promise<AxiosResponse<GamResponse<GetTracesResponse>>> {
    return this.httpClient.get<GetTracesResponse>(MapPuzzleEndpoint.TRACES, {
      queryParams: { lat: String(lat), lng: String(lng) },
    });
  }

  async openTrace(traceId: string, lat: number, lng: number): Promise<AxiosResponse<GamResponse<OpenTraceResponse>>> {
    return this.httpClient.post<OpenTraceResponse, { lat: number; lng: number }>(MapPuzzleEndpoint.TRACE_OPEN, {
      urlParams: { traceId },
      data: { lat, lng },
    });
  }

  async solveTrace(traceId: string, moveUci: string): Promise<AxiosResponse<GamResponse<SolveTraceResponse>>> {
    return this.httpClient.post<SolveTraceResponse, { moveUci: string }>(MapPuzzleEndpoint.TRACE_SOLVE, {
      urlParams: { traceId },
      data: { moveUci },
    });
  }

  async previewTrace(traceId: string): Promise<AxiosResponse<GamResponse<PreviewTraceResponse>>> {
    return this.httpClient.get<PreviewTraceResponse>(MapPuzzleEndpoint.TRACE_PREVIEW, {
      urlParams: { traceId },
    });
  }

  async viewTrace(traceId: string): Promise<AxiosResponse<GamResponse<ViewTraceResponse>>> {
    return this.httpClient.get<ViewTraceResponse>(MapPuzzleEndpoint.TRACE_VIEW, {
      urlParams: { traceId },
    });
  }
}
